import { Api } from "../Api";
import { AuthToken } from "../AuthToken";
import { DebouncedFunction } from "../DebouncedFunction";
import { LogManager } from "../logging/LogManager";
import { ObjectUtil } from "../ObjectUtil";
import { StringUtil } from "../StringUtil";
import { LocalUserSettings } from "./LocalUserSettings";
import { UserSettings } from "./UserSettings";

const log = LogManager.getLogger("core.settings.UserLayoutConfigManager");

/** Maps a localStorage key to the corresponding user_layout_config.config_type value */
const LayoutConfigTypeMap: Record<string, string> = {
    "tableColumn.settings": "C"
};

interface LayoutConfigGroup {
    configType: string;
    pendingChanges: Record<string, any>;
    debouncer: () => void;
}

/**
 * Manages persistence of user layout configurations to both localStorage and the user_layout_config table.
 * For registered keys, writes are automatically debounced and synced to the user_layout_config table.
 * For unregistered keys, writes pass through to localStorage only.
 */
export class UserLayoutConfigManager {
    private static layoutConfigGroups: Record<string, LayoutConfigGroup> = {};

    public static registerForServerSync(localSettingsKey: string) {
        const configType = LayoutConfigTypeMap[localSettingsKey];
        if (!configType) {
            log.debug(`Cannot register server sync. No LayoutConfigType mapped for storage key: ${localSettingsKey}`);
            return;
        }

        if (this.layoutConfigGroups[localSettingsKey]) return;
        this.layoutConfigGroups[localSettingsKey] = {
            configType,
            pendingChanges: {},
            debouncer: DebouncedFunction.create(() => this.persistChanges(localSettingsKey), 2000)
        };
    }

    /**
     * Populates localStorage from the server-provided layout config map.
     * Called during auth settings initialization to seed localStorage with the user's saved configs.
     * The server sends config values as JSON strings, so each value is parsed before storing.
     */
    public static populateLocalSettingsFromServer(layoutConfigs: Record<string, Record<string, string>>) {
        if (sessionStorage.getItem("hasSyncedLayoutConfigs") === "true") {
            return;
        }
        sessionStorage.setItem("hasSyncedLayoutConfigs", "true");

        // Clear out all of the possible items we might store in local storage via this mechanism.  This avoids leaving
        // a particular user's values behind for use by another user who logs in to the same browser.
        for (const storagekey of Object.keys(LayoutConfigTypeMap)) {
            LocalUserSettings.set(storagekey, null);
        }

        // Populate the values sent from the server via the user's settings
        for (const [storageKey, componentConfigs] of Object.entries(layoutConfigs)) {
            const parsed: Record<string, any> = {};
            for (const [componentKey, configValue] of Object.entries(componentConfigs)) {
                try {
                    parsed[componentKey] = JSON.parse(configValue);
                } catch {
                    parsed[componentKey] = configValue;
                }
            }
            LocalUserSettings.set(storageKey, parsed);
        }
    }

    /**
     * Saves a component's configuration to localStorage and user_layout_config table if the key is registered.
     */
    public static save(localSettingsKey: string, componentKey: string, settings: any) {
        const allSettings = LocalUserSettings.getObject(localSettingsKey, {}) as Record<string, any>;
        allSettings[componentKey] = settings;
        LocalUserSettings.set(localSettingsKey, allSettings);

        const group = this.layoutConfigGroups[localSettingsKey];
        if (group) {
            group.pendingChanges[componentKey] = settings;
            group.debouncer();
        }
    }

    /**
     * Removes a component's configuration from localStorage and user_layout_config table if the key is registered.
     */
    public static delete(localSettingsKey: string, componentKey: string) {
        const allSettings = LocalUserSettings.getObject(localSettingsKey, {}) as Record<string, any>;
        delete allSettings[componentKey];
        LocalUserSettings.set(localSettingsKey, allSettings);

        const group = this.layoutConfigGroups[localSettingsKey];
        if (group) {
            group.pendingChanges[componentKey] = undefined;
            group.debouncer();
        }
    }

    /**
     * Instantly fires off API requests for any pending layout changes across all groups.
     */
    public static async flushAllPendingChanges() {
        const promises = [];
        for (const localSettingsKey of Object.keys(this.layoutConfigGroups)) {
            promises.push(this.persistChanges(localSettingsKey));
        }
        await Promise.all(promises);
    }

    private static async persistChanges(localSettingsKey: string) {
        const group = this.layoutConfigGroups[localSettingsKey];
        if (!group) return;

        if (!AuthToken.isAuthenticated() || StringUtil.isEmptyString(UserSettings.getUserId())) {
            log.debug("Not authenticated, skipping layout config persistence.");
            group.pendingChanges = {};
            return;
        }

        const changesToFlush = { ...group.pendingChanges };
        group.pendingChanges = {};

        for (const [componentKey, settings] of Object.entries(changesToFlush)) {
            try {
                const body = {
                    user_id: UserSettings.getUserId(),
                    config_type: group.configType,
                    component_key: componentKey
                }

                if (ObjectUtil.isEmptyObject(settings)) {
                    await Api.delete("common/user-layout-config", body);
                    log.debug(`Deleted layout config from user_layout_config table [${group.configType}] - ${componentKey}`);
                } else {
                    await Api.update("common/user-layout-config", {
                        ...body,
                        config_value: JSON.stringify(settings)
                    });
                    log.debug(`Saved layout config to user_layout_config table [${group.configType}] - ${componentKey}`);
                }
            } catch (error) {
                log.error(`Failed to save layout config for ${componentKey}`, error);
            }
        }
    }
}


globalThis.window?.addEventListener("beforeunload", () => UserLayoutConfigManager.flushAllPendingChanges());

// Wrapped AuthToken listeners so they register after the circular imports resolve
Promise.resolve().then(() => {
    AuthToken.addBeforeLogoutListener(() => UserLayoutConfigManager.flushAllPendingChanges());
    AuthToken.addLoginListener(() => sessionStorage.removeItem("hasSyncedLayoutConfigs"));
});
